// src/pages/TradingData/CompositeUIHelpers.jsx
// Shared presentational components for the Composite Generator.

import React from 'react';
import { Box, Typography, Stack, Chip, alpha } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Psychology as PsychologyIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalIcon,
  SignalCellularAlt as SignalIcon,
} from '@mui/icons-material';
import { MONO } from './IndicatorConfigs';
import {
  ACCENT, WIN_COLOR, LOSS_COLOR, COMPANION_COLOR,
} from './compositeConstants';

// ── Tiny label ──────────────────────────────────────────────
export const SectionLabel = ({ children }) => (
  <Typography sx={{
    color: alpha('#fff', 0.3), fontSize: '0.52rem', fontWeight: 800,
    letterSpacing: '0.22em', textTransform: 'uppercase', fontFamily: MONO, mb: 0.6,
  }}>{children}</Typography>
);

// ── Monospace text ──────────────────────────────────────────
export const Mono = ({ children, color, size = '0.72rem', sx = {} }) => (
  <Typography sx={{ fontFamily: MONO, fontSize: size, color: color || alpha('#fff', 0.7), ...sx }}>
    {children}
  </Typography>
);

// ── Step dots ───────────────────────────────────────────────
export const StepDot = ({ n, active, done, label }) => (
  <Stack alignItems="center" spacing={0.4}>
    <Box sx={{
      width: 28, height: 28, borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: done ? WIN_COLOR : active ? ACCENT : alpha('#fff', 0.06),
      border: `2px solid ${done ? WIN_COLOR : active ? ACCENT : alpha('#fff', 0.1)}`,
      transition: 'all 0.3s',
    }}>
      {done
        ? <CheckCircleIcon sx={{ fontSize: 14, color: '#000' }} />
        : <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, fontFamily: MONO,
            color: active ? '#fff' : alpha('#fff', 0.3) }}>{n}</Typography>
      }
    </Box>
    <Typography sx={{ fontSize: '0.52rem', fontFamily: MONO, fontWeight: 700,
      color: active ? ACCENT : done ? WIN_COLOR : alpha('#fff', 0.25),
      letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{label}</Typography>
  </Stack>
);

export const StepConnector = ({ done }) => (
  <Box sx={{ flex: 1, height: 2, mx: 0.5, mt: '-10px',
    bgcolor: done ? WIN_COLOR : alpha('#fff', 0.06), transition: 'background 0.3s' }} />
);

// ── Win-rate comparison bar ─────────────────────────────────
export const WinRateBar = ({ rateTrue, rateBase, nTrue, nFalse, minTrades }) => {
  const hasData = nTrue >= minTrades;
  const delta = rateTrue - rateBase;
  const positive = delta > 0;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={0.4}>
        <Stack direction="row" spacing={0.6} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: positive ? WIN_COLOR : LOSS_COLOR }} />
          <Mono size="0.65rem" color={positive ? WIN_COLOR : LOSS_COLOR}>
            {hasData ? `${(rateTrue * 100).toFixed(1)}%` : 'n/a'} when true
          </Mono>
          <Chip label={`n=${nTrue}`} size="small" sx={{
            height: 14, fontSize: '0.5rem', fontFamily: MONO,
            bgcolor: alpha(hasData ? '#fff' : '#666', 0.06), color: alpha('#fff', 0.4),
            '& .MuiChip-label': { px: 0.5 },
          }} />
          {!hasData && (
            <Mono size="0.52rem" color={LOSS_COLOR}>below min ({minTrades})</Mono>
          )}
        </Stack>
        {hasData && (
          <Chip label={`${positive ? '+' : ''}${(delta * 100).toFixed(1)}%`} size="small" sx={{
            height: 16, fontSize: '0.58rem', fontFamily: MONO, fontWeight: 800,
            bgcolor: alpha(positive ? WIN_COLOR : LOSS_COLOR, 0.15),
            color: positive ? WIN_COLOR : LOSS_COLOR,
            border: `1px solid ${alpha(positive ? WIN_COLOR : LOSS_COLOR, 0.3)}`,
            '& .MuiChip-label': { px: 0.6 },
          }} />
        )}
      </Stack>
      <Box sx={{ height: 4, borderRadius: 2, bgcolor: alpha('#fff', 0.06), position: 'relative', overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: `${rateBase * 100}%`, bgcolor: alpha('#fff', 0.12), borderRadius: 2,
        }} />
        {hasData && (
          <Box sx={{
            position: 'absolute', left: 0, top: 0, bottom: 0,
            width: `${rateTrue * 100}%`,
            bgcolor: positive ? WIN_COLOR : LOSS_COLOR, borderRadius: 2,
            transition: 'width 0.5s ease',
          }} />
        )}
      </Box>
      <Mono size="0.52rem" color={alpha('#fff', 0.2)} sx={{ mt: 0.3 }}>
        Base: {(rateBase * 100).toFixed(1)}% — {nFalse} trades without
      </Mono>
    </Box>
  );
};

// ── Category icon / color map ───────────────────────────────
export const CATEGORY_ICONS = {
  companion: <SignalIcon sx={{ fontSize: 12 }} />,
  temporal: <CalIcon sx={{ fontSize: 12 }} />,
  compound: <PsychologyIcon sx={{ fontSize: 12 }} />,
  indicator: <TrendingUpIcon sx={{ fontSize: 12 }} />,
};
